// Shop templates, each laid out after one of the Phlox shop demos
// (shop-plant, shop-organic, shop-mobile, shop-stylist, cominsoon-pro,
// shop-jewelry). Sections render in the listed order through ShopSite.js.
// All text is our own and images are stand-in artwork.

const footerCols = (extra) => [
  ['Shop', ['New arrivals', 'Best sellers', 'Offers', 'Gift cards']],
  ['Help', ['Track order', 'Shipping', 'Returns & exchanges', 'FAQs']],
  ...extra,
];

export const SHOP_TEMPLATES = [
  {
    slug: 'plant-shop',
    name: 'Plant Shop',
    category: 'Shop',
    layout: 'shop',
    tagline: 'Bring the outdoors in.',
    description: 'A leafy plant store with trending plants, category banners, reviews and a newsletter offer.',
    colors: { primary: '#1d3b2a', accent: '#5f9e5a', bg: '#f3f6ef' },
    hero: { title: 'Grow your own indoor jungle', subtitle: 'Healthy, hand-potted plants delivered to your door.' },
    features: ['Free delivery', 'Secure payments', '14-day plant guarantee'],
    items: [],
    popular: true,
    shop: {
      brand: 'Leafy Nook',
      icon: 'plant',
      cardRatio: '3 / 4',
      sections: [
        { type: 'nav', links: [['Home', 'home'], ['Shop', 'products'], ['About', 'testimonials'], ['Contact', 'newsletter']], account: true },
        {
          type: 'hero', eyebrow: 'Plant shop', title: 'Grow your own indoor jungle', tall: true, arch: true,
          text: 'Healthy, hand-potted plants for every corner of your home, with care tips from our growers.',
          cta: 'Shop now', cta2: 'Watch our story', video: true, scrollHint: true, product: { name: 'Variegated Hoya', price: '₹1,250' },
        },
        { type: 'usp', items: [['shipping', 'Free delivery', 'On orders over ₹999'], ['secure', 'Secure payments', 'UPI, cards and wallets'], ['returns', '14-day guarantee', 'Healthy or replaced'], ['support', 'Plant help', 'Chat with a grower']] },
        { type: 'categories', items: [['nature', 'Air-purifying plants'], ['plant', 'Best sellers'], ['organic', 'Low-light plants']] },
        {
          type: 'products', id: 'products', eyebrow: 'Fresh from the nursery', title: 'Top trending plants', more: 'Shop all plants',
          items: [
            { name: 'Variegated Hoya', price: '₹1,250' },
            { name: 'Spineless Yucca', price: '₹1,600' },
            { name: 'Boston Fern', price: '₹650', badge: 'Sale', was: '₹850' },
            { name: 'Cast Iron Plant', price: '₹900' },
            { name: 'Swiss Cheese Vine', price: '₹780' },
            { name: 'Heartleaf Philodendron', price: '₹520' },
            { name: 'Kentia Palm', price: '₹2,900', badge: 'New' },
            { name: 'String of Pearls', price: '₹480' },
          ],
        },
        {
          type: 'promos', items: [
            { eyebrow: 'Accessories', title: 'Pots, tools & soil', text: 'Everything your plants need to thrive.', icon: 'tools' },
            { eyebrow: 'Safe for pets', title: 'Pet-friendly plants', text: 'Greenery your cats and dogs can live with.', icon: 'pets' },
          ],
        },
        {
          type: 'testimonials', title: 'What our plant parents say', eyebrow: 'Reviews',
          items: [
            ['Meera Iyer', 'Customer for a year', 'My fern arrived perfectly packed and is still thriving months later. The care card was really helpful.'],
            ['Rohan Kapoor', 'Customer for two years', 'Great range and quick delivery in Pune. I have filled my whole balcony from this shop.'],
            ['Fatima Shaikh', 'Customer for a year', 'They replaced a plant that arrived droopy with no questions asked. Lovely service.'],
          ],
        },
        { type: 'newsletter', id: 'newsletter', title: 'Be the first to hear', text: 'New arrivals, care tips and 10% off your first order.', cta: 'Subscribe' },
        { type: 'footer', about: 'Hand-potted plants and friendly advice, delivered across India.', columns: [['Useful links', ['Privacy policy', 'Terms of use', 'Shipping']], ['Company', ['About us', 'Our growers', 'Careers']], ['Contact', ['+91 00000 00000', 'hello@leafynook.in']], ['Address', ['12 Garden Lane', 'Bengaluru 560001']]] },
      ],
    },
  },
  {
    slug: 'organic-market',
    name: 'Organic Market',
    category: 'Shop',
    layout: 'shop',
    tagline: 'Fresh, organic and local.',
    description: 'An organic grocery with a promo slider, new products, deal of the week, best sellers and a blog.',
    colors: { primary: '#21402a', accent: '#36a85a', bg: '#fbfaf4' },
    hero: { title: 'Organic food, straight from the farm', subtitle: 'Seasonal produce and pantry staples, delivered fresh.' },
    features: ['Free delivery over ₹1,500', 'Certified organic', '7-day returns'],
    items: [],
    shop: {
      brand: 'GreenBasket',
      icon: 'organic',
      sections: [
        { type: 'topbar', text: 'Call us: +91 00000 00000' },
        { type: 'nav', links: [['Home', 'home'], ['Shop', 'products'], ['Deals', 'deal'], ['Blog', 'blog'], ['Contact', 'newsletter']], account: true },
        {
          type: 'hero', variant: 'tiles', tiles: [
            { eyebrow: 'Up to 40% off', title: 'Stay home, shop organic', text: 'Seasonal fruit, vegetables and pantry staples from certified farms, delivered fresh.', cta: 'Discover now', icon: 'organic' },
            { eyebrow: '30% off', title: 'Fresh exotic fruit', cta: 'View products', icon: 'nature' },
            { eyebrow: '25% off', title: 'Healthy breakfast', cta: 'View products', icon: 'plant' },
          ],
        },
        { type: 'usp', items: [['shipping', 'Free delivery', 'On orders over ₹1,500'], ['support', 'Support 24/7', 'We are here to help'], ['returns', '7-day returns', 'Simple and quick'], ['secure', '100% secure', 'Safe payments']] },
        {
          type: 'products', id: 'products', eyebrow: 'Just in', title: 'New products', more: 'View all products',
          items: [
            { name: 'Alphonso Mangoes', price: '₹540', was: '₹650', badge: 'Sale', tag: 'Fruit' },
            { name: 'Baby Spinach', price: '₹60', tag: 'Greens' },
            { name: 'Cold-pressed Groundnut Oil', price: '₹320', tag: 'Pantry' },
            { name: 'Red Rice', price: '₹140', tag: 'Grains' },
            { name: 'Wild Forest Honey', price: '₹450', badge: 'New', tag: 'Pantry' },
            { name: 'Cherry Tomatoes', price: '₹90', tag: 'Vegetables' },
            { name: 'Desi Cow Ghee', price: '₹780', was: '₹890', badge: 'Sale', tag: 'Dairy' },
            { name: 'Millet Muesli', price: '₹299', tag: 'Breakfast' },
          ],
        },
        {
          type: 'promos', items: [
            { eyebrow: '30% off', title: 'Make your favourite salad', text: 'Crisp greens and toppings in one box.', icon: 'nature' },
            { eyebrow: '30% off', title: 'Fresh juices & smoothies', text: 'Fruit picked for blending.', icon: 'organic' },
          ],
        },
        {
          type: 'products', id: 'best', eyebrow: 'Customer favourites', title: 'Best sellers', white: true, columns: 5,
          items: [
            { name: 'Seasonal Fruit Box', price: '₹699' },
            { name: 'Veggie Starter Box', price: '₹499' },
            { name: 'Jaggery Powder', price: '₹120' },
            { name: 'Organic Turmeric', price: '₹95' },
            { name: 'Brown Eggs (12)', price: '₹160', badge: 'New' },
          ],
        },
        { type: 'banner', eyebrow: 'This week only', title: '30% off all vegetables', text: 'Stock up on farm-fresh vegetables from local growers.', cta: 'Discover now', icon: 'plant' },
        {
          type: 'products', id: 'trending', eyebrow: 'Popular this week', title: 'Trending products',
          items: [
            { name: 'Kashmiri Apples', price: '₹220', tag: 'Fruit' },
            { name: 'Broccoli', price: '₹80', tag: 'Vegetables' },
            { name: 'A2 Milk (1 L)', price: '₹95', tag: 'Dairy' },
            { name: 'Ragi Flour', price: '₹110', was: '₹130', badge: 'Sale', tag: 'Grains' },
          ],
        },
        { type: 'deal', eyebrow: 'Deal of the week', title: 'Family Harvest Box', text: 'Eight kilos of seasonal fruit and vegetables for a family of four, picked the day before delivery.', price: '₹1,199', was: '₹1,599', cta: 'Add to cart', days: 3 },
        {
          type: 'blog', title: 'Latest from our farm', eyebrow: 'Blog',
          items: [['Recipes', 'Five quick salads for busy weekdays', '12 Oct 2026'], ['Farming', 'Meet the growers behind our mangoes', '28 Sep 2026'], ['Health', 'Why millets are back on our plates', '9 Sep 2026']],
        },
        { type: 'newsletter', id: 'newsletter', title: 'Get 20% off your first order', text: 'Join our list for weekly offers and seasonal recipes.', cta: 'Subscribe' },
        { type: 'footer', about: 'We deliver organic food across the city, free on orders over ₹1,500.', columns: footerCols([['Contact', ['hello@greenbasket.in', '+91 00000 00000', 'Your street, your city']]]) },
      ],
    },
  },
  {
    slug: 'mobile-store',
    name: 'Mobile Store',
    category: 'Shop',
    layout: 'shop',
    tagline: 'The latest phones, best prices.',
    description: 'A smartphone and gadget store with a featured phone hero, offer banners, featured items and brands.',
    colors: { primary: '#0f172a', accent: '#0ea5b7', bg: '#f3f6fa' },
    hero: { title: 'Your next smartphone is here', subtitle: 'Genuine phones and accessories with brand warranty.' },
    features: ['Brand warranty', 'No-cost EMI', 'Express delivery'],
    items: [],
    shop: {
      brand: 'PhoneBay',
      icon: 'mobile',
      sections: [
        { type: 'nav', subtitle: 'Electronics shop', links: [['Home', 'home'], ['Phones', 'products'], ['Featured', 'featured'], ['Brands', 'featured'], ['Accessories', 'new']] },
        {
          type: 'hero', dark: true, eyebrow: 'Smartphone store', title: 'Nova X12 Pro', price: '₹54,999', was: '₹64,999',
          text: 'A 6.7-inch display, triple camera and two-day battery. Exchange your old phone and save more.',
          cta: 'Buy now', cta2: 'Discover all products',
        },
        {
          type: 'products', id: 'products', eyebrow: 'Up to 30% off', title: 'Popular products',
          items: [
            { name: 'Nova X10', price: '₹34,999', tag: 'Nova' },
            { name: 'Pixelon 8', price: '₹48,999', was: '₹59,999', badge: '-18%', tag: 'Pixelon' },
            { name: 'Orbit M5', price: '₹18,499', tag: 'Orbit' },
            { name: 'Nova X12', price: '₹44,999', tag: 'Nova' },
          ],
        },
        { type: 'banner', eyebrow: 'Up to 10% off', title: 'Smart home speakers', text: 'Voice assistants, smart displays and streaming sticks.', cta: 'Buy now', icon: 'audio' },
        {
          type: 'products', id: 'featured', eyebrow: 'Hand-picked', title: 'Featured items', white: true,
          items: [
            { name: 'Pixelon 7 XL', price: '₹39,999' },
            { name: 'Lumen 14', price: '₹69,900', badge: 'New' },
            { name: 'Nova X12 Pro', price: '₹54,999', was: '₹64,999', badge: 'Sale' },
            { name: 'Orbit M7 Plus', price: '₹24,999' },
            { name: 'Zenta Z2', price: '₹15,999' },
            { name: 'Pixelon 6', price: '₹29,999' },
            { name: 'Nova X9', price: '₹21,999', was: '₹26,999', badge: 'Sale' },
            { name: 'Lumen 14 Max', price: '₹89,900' },
          ],
        },
        { type: 'banner', eyebrow: 'Up to 10% off', title: 'Wireless earbuds', text: 'Noise cancelling, 30-hour battery and fast pairing.', cta: 'Buy now', icon: 'audio' },
        {
          type: 'products', id: 'new', eyebrow: 'Just launched', title: 'New products',
          items: [
            { name: 'Lumen 14', price: '₹69,900' },
            { name: 'Orbit Watch 3', price: '₹7,999', icon: 'bolt' },
            { name: 'PowerCube 20K', price: '₹1,999', icon: 'bolt' },
            { name: 'Pixelon Buds', price: '₹5,499', icon: 'audio' },
          ],
        },
        { type: 'banner', eyebrow: 'Camera phones', title: 'Best selfie camera phones', text: 'Front cameras that shine in low light, from ₹14,999.', cta: 'View more', icon: 'camera' },
        { type: 'brands', title: 'Shop by brand', items: ['NOVA', 'PIXELON', 'ORBIT', 'LUMEN', 'ZENTA'] },
        {
          type: 'footer', about: 'Genuine smartphones and accessories with brand warranty and no-cost EMI.',
          columns: [
            ['Customer service', ['Contact us', 'Order status', 'Give feedback', 'Returns & exchanges']],
            ['Services', ['Phone repair', 'Screen protection', 'Gift cards', 'Get our app']],
            ['Newsletter', []],
          ],
        },
      ],
    },
  },
  {
    slug: 'stylist',
    name: 'Stylist',
    category: 'Shop',
    layout: 'shop',
    tagline: 'Style that feels like you.',
    description: 'A personal stylist and fashion blogger site with services, a small shop and fashion news.',
    colors: { primary: '#2a2124', accent: '#c4846f', bg: '#faf4f0' },
    hero: { title: 'Style that feels like you', subtitle: 'Personal styling, wardrobe edits and a curated shop.' },
    features: ['Personal styling', 'Wardrobe edits', 'Curated collection'],
    items: [],
    shop: {
      brand: 'Ananya Rao',
      icon: 'stylist',
      serif: true,
      sections: [
        { type: 'nav', links: [['Home', 'home'], ['About', 'about'], ['Services', 'services'], ['Shop', 'products'], ['Blog', 'blog']], search: false, cta: 'Book a session' },
        { type: 'hero', variant: 'personal', eyebrow: 'Stylist & fashion blogger', title: 'Style is all that matters', name: 'Ananya Rao', text: 'I help busy people build wardrobes they love, with honest advice, smart shopping and outfits for every occasion.', cta: 'Read more' },
        { type: 'about', eyebrow: 'About me', title: 'Living my style, every day', badgeLabel: 'Styling since', badge: '2014', text: 'I started styling friends for weddings and job interviews, and it grew into a studio in Mumbai. Today I work with clients across India online and in person, mixing local designers with everyday basics.', cta: 'About me' },
        {
          type: 'services', eyebrow: 'Services', title: 'How can I help?', cta: 'More services',
          items: [['brush', 'Fashion design', 'Custom pieces designed around your shape, colours and lifestyle.'], ['stylist', 'What to wear', 'Outfit plans for work, travel, festivals and big days.'], ['sparkle', 'Trend edits', 'Seasonal picks that fit what you already own.']],
        },
        {
          type: 'products', id: 'products', eyebrow: 'Shop', title: 'New collection', columns: 3, more: 'See more',
          items: [
            { name: 'Indigo Utility Jumpsuit', price: '₹3,400' },
            { name: 'Crisp Cotton Shirt', price: '₹1,900' },
            { name: 'Camel Wrap Coat', price: '₹5,600', badge: 'New' },
          ],
        },
        {
          type: 'blog', title: 'Fashion news', eyebrow: 'Journal',
          items: [['Runway', 'Backstage at Lakmé Fashion Week', '18 Oct 2026'], ['Designers', 'Five Indian labels to watch this winter', '2 Oct 2026'], ['Beauty', 'Easy care tips for curly hair', '20 Sep 2026']],
        },
        { type: 'cta', eyebrow: 'Ready for a change?', title: 'Let me make your style better', cta: 'Contact me', photo: true },
        { type: 'footer', about: 'Personal styling studio in Mumbai, working with clients across India.', columns: [['Contact', ['hello@ananyarao.style', '+91 00000 00000', 'Bandra West, Mumbai']]] },
      ],
    },
  },
  {
    slug: 'coming-soon',
    name: 'Coming Soon',
    category: 'Shop',
    layout: 'shop',
    tagline: 'Launching soon.',
    description: 'A one-screen launch page with a live countdown, email sign-up and social links.',
    colors: { primary: '#120f2b', accent: '#7c5cff', bg: '#120f2b' },
    hero: { title: 'We are coming soon', subtitle: 'Sign up to hear the moment we launch.' },
    features: ['Live countdown', 'Email sign-up', 'Social links'],
    items: [],
    shop: {
      brand: 'Your Brand',
      icon: 'soon',
      sections: [
        { type: 'comingSoon', eyebrow: 'Under construction', title: 'We are coming soon', text: 'Our new online store is almost ready. Leave your email and we will send you an early-bird discount on launch day.', days: 21 },
      ],
    },
  },
  {
    slug: 'jewelry-shop',
    name: 'Jewelry Shop',
    category: 'Shop',
    layout: 'shop',
    tagline: 'Every piece has a story.',
    description: 'An elegant jewellery store with offer hero, filtered new arrivals, sale grid, reviews and newsletter.',
    colors: { primary: '#2b2420', accent: '#b48a4f', bg: '#fbf7f1' },
    hero: { title: 'Every piece has a story', subtitle: 'Hallmarked gold and silver, made by hand.' },
    features: ['Certified hallmark', 'Two-year warranty', 'Free insured shipping'],
    items: [],
    popular: true,
    shop: {
      brand: 'Aurelia',
      icon: 'jewelry',
      cardRatio: '470 / 418',
      serif: true,
      sections: [
        { type: 'nav', links: [['Home', 'home'], ['New arrivals', 'products'], ['Deals', 'deals'], ['Contact', 'newsletter']], account: true, wishlist: true },
        { type: 'hero', bigBadge: '20%', bigBadgeText: 'off all earrings this week', eyebrow: 'Limited-time offer', title: 'Every piece has a story', text: 'Hallmarked gold and silver jewellery, made by hand in small batches by our Jaipur artisans.', cta: 'Buy now', arch: true },
        { type: 'usp', items: [['quality', 'High quality', 'BIS hallmarked'], ['secure', 'Two-year warranty', 'On every piece'], ['shipping', 'Free shipping', 'Insured, over ₹5,000'], ['support', '24/7 support', 'Talk to an expert']] },
        {
          type: 'products', id: 'products', eyebrow: 'Fresh designs', title: 'New arrivals', more: 'View more',
          tabs: ['All', "Men's accessories", "Women's accessories", 'Rings', 'Earrings'],
          items: [
            { name: 'Gold Drop Earrings', price: '₹8,550', was: '₹9,950', badge: 'Sale' },
            { name: 'Solitaire Ring', price: '₹24,000' },
            { name: 'Twisted Bangle', price: '₹15,500' },
            { name: 'Pearl Choker', price: '₹12,800', badge: 'New' },
            { name: "Men's Signet Ring", price: '₹9,200' },
            { name: 'Kundan Studs', price: '₹4,600', was: '₹5,400', badge: 'Sale' },
            { name: 'Silver Anklet Pair', price: '₹2,900' },
            { name: 'Rose Gold Pendant', price: '₹7,400' },
          ],
        },
        { type: 'banner', eyebrow: 'Experience elegance', title: 'Get 30% off on rings', text: 'Shine every day with our festive offers.', cta: 'Buy now', icon: 'jewelry' },
        { type: 'marquee', label: 'New sale', text: '30% off' },
        {
          type: 'products', id: 'deals', eyebrow: 'Ends Sunday', title: 'Last chance for great prices', white: true,
          items: [
            { name: 'Temple Necklace', price: '₹18,900', was: '₹23,500', badge: '-20%' },
            { name: 'Diamond Studs', price: '₹21,000', was: '₹26,000', badge: '-19%' },
            { name: 'Charm Bracelet', price: '₹3,200', was: '₹3,900', badge: '-18%' },
            { name: 'Jhumka Earrings', price: '₹6,300', was: '₹7,400', badge: '-15%' },
          ],
        },
        {
          type: 'testimonials', eyebrow: 'Reviews', title: 'Loved by our customers',
          items: [
            ['Kavya Menon', 'Bride, Kochi', 'My wedding set was even more beautiful in person. They resized the bangles for free.'],
            ['Sneha Joshi', 'Customer, Pune', 'Beautiful packaging and the hallmark certificate came with the order. Will buy again.'],
            ['Aditi Verma', 'Customer, Delhi', 'The pendant I gifted my mother is perfect. Delivery was quick and fully insured.'],
            ['Nisha Pillai', 'Customer, Chennai', 'Lovely craftsmanship on the jhumkas and the team helped me pick the right size.'],
          ],
        },
        {
          type: 'promos', items: [
            { eyebrow: 'Special pricing', title: 'Shop our festive offers', text: 'Special prices across the collection.', cta: 'Buy now' },
            { eyebrow: 'Up to 40% off', title: 'Selected silver pieces', text: 'Everyday silver at its best price.', cta: 'Buy now', icon: 'sparkle' },
          ],
        },
        { type: 'newsletter', id: 'newsletter', title: 'Subscribe to our newsletter', text: 'New collections, care guides and members-only offers.', cta: 'Subscribe now', sideArt: true },
        {
          type: 'footer', about: 'Handmade, hallmarked jewellery from Jaipur, shipped insured across India.',
          columns: [
            ['Quick links', ['About us', 'Contact us', 'Privacy policy', 'Terms of service', 'FAQs']],
            ['Discover', ['Customer reviews', 'Loyalty programme', 'Best sellers', 'New arrivals']],
            ['Customer care', ['Seasonal collections', 'Jewellery care', 'Returns & exchanges', 'Shop all']],
          ],
        },
      ],
    },
  },
];
